import { useState, useRef, useEffect } from 'react';
import { Head, router } from '@inertiajs/react';
import { MainLayout } from '../../layouts/MainLayout';
import { route } from '../../utils/route';
import { WhatsAppIcon } from '../../components/icons/WhatsAppIcon';

interface TransferReceiptProps {
  order: {
    id: number;
    formatted_number: string;
    total: number;
  };
  canUpload: boolean;
  invoiceUrl: string | null;
  shouldWaitForInvoice: boolean;
  receipt: {
    original_filename: string;
    uploaded_at: string;
    verified_at: string | null;
  } | null;
  bankAccount: {
    bank: string;
    holder: string;
    cbu: string;
    alias: string;
    cuit: string;
    tipo: string;
  } | null;
  whatsapp: {
    url: string;
  };
  flash?: {
    success?: string;
  };
}

export default function Show({ order, receipt, bankAccount, whatsapp, flash, canUpload, invoiceUrl: initialInvoiceUrl, shouldWaitForInvoice }: TransferReceiptProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState(!!flash?.success);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [invoiceUrl, setInvoiceUrl] = useState<string | null>(initialInvoiceUrl);

  useEffect(() => {
    if (!shouldWaitForInvoice || invoiceUrl) return;

    const interval = setInterval(async () => {
      try {
        const res = await fetch(`/compras/${order.id}/invoice-status`);
        if (!res.ok) return;
        const data = await res.json();
        if (data.has_invoice && data.invoice_url) {
          setInvoiceUrl(data.invoice_url);
        }
      } catch {
        // silently ignore polling errors
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [shouldWaitForInvoice, invoiceUrl, order.id]);



  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      validateAndSetFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      validateAndSetFile(e.target.files[0]);
    }
  };

  const validateAndSetFile = (file: File) => {
    setError(null);
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      setError('Solo se permiten archivos JPG, PNG o PDF.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('El archivo no puede superar los 10MB.');
      return;
    }
    setSelectedFile(file);
  };

  const handleUpload = () => {
    if (!selectedFile) return;

    setIsUploading(true);
    setError(null);

    const formData = new FormData();
    formData.append('file', selectedFile);

    router.post(`/compras/${order.id}/comprobante`, formData, {
      forceFormData: true,
      onSuccess: () => {
        setSelectedFile(null);
        setUploadSuccess(true);
      },
      onError: (errors) => {
        const msg = errors.file || 'Error al subir el comprobante. Intentá de nuevo.';
        setError(msg);
      },
      onFinish: () => {
        setIsUploading(false);
      },
    });
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const formatPrice = (price: number) =>
    new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(price);

  return (
    <MainLayout>
      <Head>
        <title>Comprobante de transferencia | Abasto Virtual</title>
        <meta name="robots" content="noindex" />
      </Head>

      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-gray-100 px-4 py-10 md:py-16">
        <div className="mx-auto max-w-md md:max-w-3xl">
          <div className="rounded-2xl bg-white p-6 shadow-xl shadow-gray-200/50 md:p-8">
            {/* Header */}
            <div className="mb-6 text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-blue-100">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-7 w-7 text-blue-600">
                  <path d="M3 21h18M3 7v14M21 7v14M6 21V11M12 21V11M18 21V11M6 7h12M12 3L22 7H2L12 3z" />
                </svg>
              </div>
              <h1 className="text-xl font-bold text-gray-900 md:text-2xl">Transferencia Bancaria</h1>
              <p className="mt-1 text-sm text-gray-500">Pedido {order.formatted_number}</p>
            </div>

            {/* Invoice banner */}
            {(shouldWaitForInvoice || invoiceUrl) && (
              <div className={`mb-6 rounded-xl border p-4 ${invoiceUrl ? 'border-green-200 bg-green-50' : 'border-blue-200 bg-blue-50'}`}>
                {invoiceUrl ? (
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <svg className="h-5 w-5 flex-shrink-0 text-green-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="9 12 12 15 16 10" />
                      </svg>
                      <span className="text-sm font-medium text-green-800">Factura emitida</span>
                    </div>
                    <a
                      href={invoiceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white no-underline transition-colors hover:bg-green-700 hover:text-white"
                    >
                      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      Descargar factura
                    </a>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <span className="h-5 w-5 animate-spin rounded-full border-2 border-blue-300 border-t-blue-600" />
                    <span className="text-sm font-medium text-blue-800">Emitiendo factura...</span>
                  </div>
                )}
              </div>
            )}

            {/* Alias + Amount + Bank details */}
            {bankAccount ? (
              <div className="mb-6 grid gap-4 md:grid-cols-2">
                <div className="space-y-4 rounded-xl border-2 border-blue-200 bg-blue-50/30 p-4">
                  <CopyableField
                    label="Alias"
                    value={bankAccount.alias}
                    copyValue={bankAccount.alias}
                    onCopy={copyToClipboard}
                  />
                  <CopyableField
                    label="Importe a transferir"
                    value={'$' + Math.round(order.total).toLocaleString('es-AR')}
                    copyValue={String(Math.round(order.total))}
                    onCopy={copyToClipboard}
                  />
                </div>

                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                  <h2 className="mb-3 text-sm font-semibold text-gray-500">Datos bancarios</h2>
                  <div className="space-y-2 text-sm">
                    <BankInfoLine label="CBU" value={bankAccount.cbu} />
                    <BankInfoLine label="Titular" value={bankAccount.holder} />
                    <BankInfoLine label="CUIT" value={bankAccount.cuit} />
                    <BankInfoLine label="Banco" value={bankAccount.bank} />
                    <BankInfoLine label="Tipo" value={bankAccount.tipo} />
                  </div>
                </div>
              </div>
            ) : (
              <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4">
                <p className="text-sm font-medium text-amber-800">
                  No pudimos mostrar los datos bancarios en este momento.
                </p>
                <p className="mt-1 text-sm text-amber-700">
                  Escribinos por WhatsApp y te los pasamos al instante.
                </p>
                <a
                  href={whatsapp.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 inline-flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white no-underline transition-colors hover:bg-green-700 hover:text-white"
                >
                  <WhatsAppIcon className="h-4 w-4" />
                  Pedir datos por WhatsApp
                </a>
              </div>
            )}

            {/* Upload success */}
            {(uploadSuccess || receipt) && (
              <div className="mb-6 rounded-xl border border-green-200 bg-green-50 p-4">
                <div className="flex items-start gap-3">
                  <svg className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="9 12 12 15 16 10" />
                  </svg>
                  <div>
                    {receipt?.verified_at ? (
                      <p className="text-sm font-medium text-green-800">Transferencia verificada el {receipt.verified_at}.</p>
                    ) : (
                      <>
                        <p className="text-sm font-medium text-green-800">Comprobante subido correctamente.</p>
                        <p className="mt-1 text-xs text-green-700">Archivo: {receipt?.original_filename}</p>
                        <p className="mt-1 text-xs text-green-600">Te avisaremos cuando verifiquemos tu pago.</p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Send receipt — two alternative options */}
            {canUpload && (
              <div className="mb-6">
                <h2 className="mb-3 text-sm font-semibold text-gray-800">
                  {receipt ? 'Reemplazar comprobante' : 'Enviá tu comprobante'}
                </h2>
                <p className="mb-4 text-xs text-gray-500">Elegí una de las dos opciones:</p>

                <div className="grid gap-4 md:grid-cols-[1fr_auto_1fr]">
                  {/* Option A: Upload */}
                  <div className="flex flex-col rounded-xl border border-gray-200 bg-white p-4">
                    <div className="mb-3 flex items-center gap-2">
                      <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-50 text-xs font-bold text-blue-500">A</span>
                      <h3 className="text-sm font-semibold text-gray-800">Subir archivo acá</h3>
                    </div>

                    <div
                      className={`relative flex-1 cursor-pointer rounded-xl border-2 border-dashed p-6 text-center transition-colors ${
                        dragActive ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
                      }`}
                      onDragEnter={handleDrag}
                      onDragLeave={handleDrag}
                      onDragOver={handleDrag}
                      onDrop={handleDrop}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp,application/pdf"
                        onChange={handleFileSelect}
                        className="hidden"
                      />
                      <svg className="mx-auto mb-2 h-8 w-8 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      {selectedFile ? (
                        <p className="text-sm font-medium text-blue-600">{selectedFile.name}</p>
                      ) : (
                        <>
                          <p className="text-sm text-gray-600">Arrastrá tu comprobante acá o hacé click</p>
                          <p className="mt-1 text-xs text-gray-400">JPG, PNG o PDF (max 10MB)</p>
                        </>
                      )}
                    </div>

                    {error && (
                      <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
                        {error}
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={handleUpload}
                      disabled={isUploading || !selectedFile}
                      className={`mt-4 flex w-full items-center justify-center gap-2 rounded-xl px-6 py-3 text-sm font-semibold transition-all ${
                        isUploading || !selectedFile ? 'cursor-not-allowed bg-gray-100 text-gray-400' : 'bg-green-500 text-white hover:bg-green-600 active:scale-[0.98]'
                      }`}
                    >
                      {isUploading ? (
                        <>
                          <span className="h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                          Subiendo...
                        </>
                      ) : (
                        <>
                          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          {receipt ? 'Reemplazar comprobante' : 'Subir comprobante'}
                        </>
                      )}
                    </button>
                  </div>

                  {/* Divider */}
                  <div className="flex items-center justify-center md:flex-col">
                    <div className="h-px flex-1 bg-gray-200 md:h-auto md:w-px md:flex-1" />
                    <span className="px-3 text-xs font-medium text-gray-400 md:py-3 md:px-0">o</span>
                    <div className="h-px flex-1 bg-gray-200 md:h-auto md:w-px md:flex-1" />
                  </div>

                  {/* Option B: WhatsApp */}
                  <div className="flex flex-col rounded-xl border border-gray-200 bg-white p-4">
                    <div className="mb-3 flex items-center gap-2">
                      <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[#25D366]/10 text-xs font-bold text-[#25D366]">B</span>
                      <h3 className="text-sm font-semibold text-gray-800">Por WhatsApp</h3>
                    </div>

                    <ol className="mb-3 flex-1 space-y-2 text-[13px] text-gray-600">
                      <li className="flex gap-3">
                        <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-[11px] font-bold text-gray-500">1</span>
                        <span>Tocá el botón de abajo para abrirnos un chat y enviarnos un mensaje en WhatsApp.</span>
                      </li>
                      <li className="flex gap-3">
                        <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-[11px] font-bold text-gray-500">2</span>
                        <span>Realizá la transferencia desde tu banco.</span>
                      </li>
                      <li className="flex gap-3">
                        <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-[11px] font-bold text-gray-500">3</span>
                        <span>Compartí el comprobante desde la app de tu banco directo al chat que ya tenés abierto.</span>
                      </li>
                    </ol>

                    <a
                      href={whatsapp.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-[#25D366] px-6 py-3 text-sm font-semibold text-white no-underline transition-colors hover:bg-[#1da851] hover:text-white"
                    >
                      <WhatsAppIcon className="h-5 w-5" />
                      Abrir chat en WhatsApp
                    </a>
                  </div>
                </div>
              </div>
            )}

            {/* Link to orders */}
            <a
              href={route('orders.index')}
              className="flex w-full items-center justify-center gap-2 rounded-xl border border-gray-300 bg-gray-50 px-6 py-3 text-sm font-medium text-gray-700 no-underline transition-colors hover:bg-gray-100"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 12H5M12 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Ver mis pedidos
            </a>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}

function CopyableField({ label, value, copyValue, onCopy }: { label: string; value: string; copyValue: string; onCopy: (v: string) => void }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    onCopy(copyValue);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="text-center">
      <span className="text-xs font-medium text-gray-500">{label}</span>
      <button
        type="button"
        onClick={handleCopy}
        className="relative mt-1 flex w-full items-center justify-center rounded-lg border border-blue-200 bg-blue-50 px-10 py-3 transition-colors hover:bg-blue-100"
      >
        <span className="text-2xl font-bold text-blue-900">{value}</span>
        <span className="absolute right-3 flex h-5 w-12 items-center justify-end">
          {copied ? (
            <svg className="h-5 w-5 text-green-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          ) : (
            <svg className="h-5 w-5 text-blue-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="9" y="9" width="13" height="13" rx="2" />
              <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
            </svg>
          )}
        </span>
      </button>
    </div>
  );
}

function BankInfoLine({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between py-0.5">
      <span className="text-blue-600">{label}</span>
      <span className="font-medium text-blue-900">{value}</span>
    </div>
  );
}
